import {
  ClientProfileDisplayNamePattern,
  ClientProfileRelationshipType,
} from '@/types/schema';

export interface ClientProfileRelationshipFormShape {
  relationshipId: string | null;
  toClientProfileId: string;
  display: string;
  // this needs to be an empty string because this is used to prepopulate the value
  // of a select dropdown, and `''` is a valid value, while `null` isn't.
  type: ClientProfileRelationshipType | '';
}

export interface ClientProfileNameFields {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
}

export interface ClientProfileFormShape extends ClientProfileNameFields {
  id: string | null;
  /**
   * When set, takes precedence over the legal name everywhere the client is
   * displayed. Deliberately not part of ClientProfileNameFields: that type is
   * for prefilling a legal name parsed out of a document or AI suggestion,
   * which never carries a preferred name.
   */
  preferredName: string;
  displayNamePattern: ClientProfileDisplayNamePattern;
  email: string;
  address: {
    id: string | null;
    cityName: string;
    stateCode: string;
  };
  dateOfBirth: Date | null;
  dateOfDeath: Date | null;
  isGrantor: boolean;
  // this can be present when the individual is a primary client, but isn't always
  isPrimary?: boolean;
  relationships: ClientProfileRelationshipFormShape[];
  notes: string;
  isDeceased: boolean;
}

interface GetDefaultFormValuesOpts {
  hasGrantorEnabled?: boolean;
  relationshipOptions: ClientProfileRelationshipFormShape[];
  householdId: string;
  initialClientProfileName?: ClientProfileNameFields;
}

export const getDefaultClientProfileFormValues = ({
  hasGrantorEnabled,
  relationshipOptions,
  initialClientProfileName,
}: GetDefaultFormValuesOpts): ClientProfileFormShape => {
  return {
    id: null,
    firstName: initialClientProfileName?.firstName ?? '',
    middleName: initialClientProfileName?.middleName ?? '',
    lastName: initialClientProfileName?.lastName ?? '',
    suffix: initialClientProfileName?.suffix ?? '',
    preferredName: '',
    displayNamePattern: ClientProfileDisplayNamePattern.FirstLastSuffix,
    email: '',
    address: {
      id: null,
      cityName: '',
      stateCode: '',
    },
    dateOfBirth: null,
    dateOfDeath: null,
    isGrantor: hasGrantorEnabled ?? false,
    isPrimary: false,
    relationships: relationshipOptions,
    notes: '',
    isDeceased: false,
  };
};
